/**
 * Difficulty profiles.
 *
 * These are *scalars applied to the baseline in tuning.ts*, not a second set of
 * constants. Normal is deliberately all-1.0, so the numbers we actually
 * playtested remain the single source of truth and the other two tiers are
 * expressed as deviations from them. Retuning the game still means editing
 * tuning.ts alone.
 *
 * The split below matters more than the values:
 *
 *   World shape  — tower, item and hiding-spot counts feed world generation,
 *                  which the client and server each run from the seed. These
 *                  must be identical on both sides or the two build different
 *                  forests, so difficulty travels with the seed and is applied
 *                  inside worldgen.
 *
 *   Behaviour    — speeds, perception, pacing, timings. The server owns all of
 *                  it; the client never needs to agree.
 */

export const Difficulty = {
  Casual: 0,
  Normal: 1,
  Hard: 2,
} as const;
export type Difficulty = (typeof Difficulty)[keyof typeof Difficulty];

export const DIFFICULTY_NAMES: Record<Difficulty, string> = {
  [Difficulty.Casual]: 'Casual',
  [Difficulty.Normal]: 'Normal',
  [Difficulty.Hard]: 'Hard',
};

export const DIFFICULTY_BLURBS: Record<Difficulty, string> = {
  [Difficulty.Casual]:
    'Two towers. He is slower than you and half-blind, and will not grab you unless you run straight into him — built for learning the mechanics, not the hunt.',
  [Difficulty.Normal]: 'Three towers. The forest as intended.',
  [Difficulty.Hard]:
    'Every tower. He hears further, holds a chase, and the dark lasts longer.',
};

export interface DifficultyProfile {
  // ---- World shape: must match on both sides ------------------------------
  /** How many towers exist in the world. */
  towerCount: number;
  /** How many must be activated to call for rescue. */
  towersRequired: number;
  /** Multiplier on every item spawn count. */
  itemCount: number;
  /** Total hiding places. */
  hidingCount: number;

  // ---- Behaviour: server-side only ----------------------------------------
  /** Multiplier on how fast alertness accrues. */
  alertnessGain: number;
  /** Multiplier on chase speed. */
  chaseSpeed: number;
  /** Multiplier on how far he can see. */
  viewRange: number;
  /** Multiplier on how far sound carries to him. */
  hearingRange: number;
  /** Multiplier on how long he pursues after losing contact. */
  chaseLoseTime: number;
  /**
   * Multiplier on how close he must be to actually grab you.
   *
   * Normally this is generous — a proximity radius, not a touch. Casual
   * shrinks it down to roughly his own collision radius, so the only way he
   * catches you is running into him, not simply being caught by a chase.
   */
  grabRadius: number;
  /** Multiplier on the opening grace period. */
  openingGrace: number;
  /** Multiplier on how long he must be absent before the Director nudges him. */
  prowlThreshold: number;
  /** Multiplier on how long a flare holds his attention. */
  flareDistraction: number;
  /** Multiplier on the radius an activated tower protects. */
  safeRadius: number;

  // ---- Player-side timings ------------------------------------------------
  /** Multiplier on seconds of held signal needed to activate a tower. */
  transmitDuration: number;
  /** Multiplier on the width of the on-signal band. */
  transmitTolerance: number;
  /** Multiplier on flashlight battery drain. */
  batteryDrain: number;
  /**
   * Multiplier on how fast sprinting empties the bar. *Lower is kinder* — this
   * one runs the opposite way to most of the fields above, so Casual gets the
   * small number.
   *
   * Sprint duration is really an escape-distance budget: at 6 m/s, halving the
   * drain is thirty-odd extra metres of running, which is roughly the
   * difference between breaking line of sight and not.
   */
  staminaDrain: number;
  /** Multiplier on how fast the bar refills. Higher is kinder. */
  staminaRegen: number;
}

const CASUAL: DifficultyProfile = {
  towerCount: 5,
  towersRequired: 2,
  itemCount: 1.5,
  hidingCount: 34,

  alertnessGain: 0.7,
  // Slower than a sprint, deliberately: regaining control after a
  // confrontation should mean you can just turn and run, not merely that
  // breaking line of sight buys a chance. He is still frightening up close —
  // that is what the confrontation and the shrunken grabRadius are for — but
  // a straight footrace is one you win.
  chaseSpeed: 0.65,
  viewRange: 0.75,
  hearingRange: 0.8,
  chaseLoseTime: 0.7,
  // A proximity grab reads as unfair once he can no longer outrun you — you
  // would be caught by the chase itself rather than by a mistake. Shrunk to
  // roughly his own collision radius (MONSTER.RADIUS + PLAYER.RADIUS ≈ 1.95m)
  // so a grab means you ran into him, not that he caught up to you.
  grabRadius: 0.6,
  openingGrace: 1.6,
  prowlThreshold: 1.5,
  flareDistraction: 1.5,
  safeRadius: 1.35,

  transmitDuration: 0.75,
  transmitTolerance: 1.35,
  batteryDrain: 0.7,
  /**
   * Five times the sprint before the bar empties — near half a minute of it.
   *
   * On the other tiers stamina is the leash that stops you simply outrunning
   * the game, so it has to bite. Here it does not need to: he is already
   * slower than a sprinting player, so the bar is not what keeps him
   * dangerous. Leaving it short only punished the players this tier exists
   * for — someone learning the forest, or testing a mechanic, who spends the
   * whole run holding shift.
   */
  staminaDrain: 0.2,
  staminaRegen: 1.7,
};

const NORMAL: DifficultyProfile = {
  towerCount: 5,
  towersRequired: 3,
  itemCount: 1,
  hidingCount: 26,

  alertnessGain: 1,
  chaseSpeed: 1,
  viewRange: 1,
  hearingRange: 1,
  chaseLoseTime: 1,
  grabRadius: 1,
  openingGrace: 1,
  prowlThreshold: 1,
  flareDistraction: 1,
  safeRadius: 1,

  transmitDuration: 1,
  transmitTolerance: 1,
  batteryDrain: 1,
  staminaDrain: 1,
  staminaRegen: 1,
};

const HARD: DifficultyProfile = {
  towerCount: 5,
  // Every tower in the world. There is no spare, so a tower you cannot reach
  // is a run you cannot finish.
  towersRequired: 5,
  itemCount: 0.65,
  hidingCount: 18,

  alertnessGain: 1.35,
  chaseSpeed: 1.08,
  viewRange: 1.25,
  hearingRange: 1.25,
  chaseLoseTime: 1.5,
  grabRadius: 1,
  openingGrace: 0.5,
  prowlThreshold: 0.6,
  flareDistraction: 0.7,
  safeRadius: 0.75,

  transmitDuration: 1.3,
  transmitTolerance: 0.8,
  batteryDrain: 1.4,
  // Only a nudge. Sprinting is already a last resort at this tier because of
  // how loud it is; making the bar tiny as well would just remove the option.
  staminaDrain: 1.15,
  staminaRegen: 0.85,
};

const PROFILES: Record<Difficulty, DifficultyProfile> = {
  [Difficulty.Casual]: CASUAL,
  [Difficulty.Normal]: NORMAL,
  [Difficulty.Hard]: HARD,
};

/** Unknown values fall back to Normal rather than throwing. */
export function profileFor(difficulty: number): DifficultyProfile {
  return PROFILES[difficulty as Difficulty] ?? NORMAL;
}

export function isDifficulty(value: number): value is Difficulty {
  return value === Difficulty.Casual || value === Difficulty.Normal || value === Difficulty.Hard;
}
